/* eslint-disable new-cap */
import localforage from 'localforage'
import * as AES from 'aes-js'
import configs from '@/config'
import sentry from '@/services/sentry'
import { clearSavedProfiles } from '@/utils/indexedDB/shared-frame-cache'

const currentUserCacheKey = 'aha-currentUser'
const unleashContextCacheKey = 'aha-flagContext'

const encrypt = (text) => {
  if (!configs.cryptoEncryptionKey) return text
  try {
    const key = AES.utils.hex.toBytes(configs.cryptoEncryptionKey)
    const textBytes = AES.utils.utf8.toBytes(text)

    const padding = 16 - (textBytes.length % 16)
    const paddedBytes = new Uint8Array(textBytes.length + padding)
    paddedBytes.set(textBytes)
    for (let i = 0; i < padding; i++) {
      paddedBytes[textBytes.length + i] = padding
    }

    const iv = window.crypto.getRandomValues(new Uint8Array(16))
    const aesCbc = new AES.ModeOfOperation.cbc(key, iv)
    const encryptedBytes = aesCbc.encrypt(paddedBytes)

    return `${AES.utils.hex.fromBytes(iv)}:${AES.utils.hex.fromBytes(encryptedBytes)}`
  } catch (error) {
    sentry.captureException(error)
    return text
  }
}

const decrypt = (encryptedText) => {
  if (!configs.cryptoEncryptionKey || !encryptedText || typeof encryptedText !== 'string' || !encryptedText.includes(':')) {
    return encryptedText
  }
  try {
    const key = AES.utils.hex.toBytes(configs.cryptoEncryptionKey)
    const parts = encryptedText.split(':')
    if (parts.length !== 2) return encryptedText

    const iv = AES.utils.hex.toBytes(parts[0])
    const encryptedBytes = AES.utils.hex.toBytes(parts[1])

    const aesCbc = new AES.ModeOfOperation.cbc(key, iv)
    const decryptedBytes = aesCbc.decrypt(encryptedBytes)

    // PKCS7 padding removal
    const padding = decryptedBytes[decryptedBytes.length - 1]
    if (padding > 0 && padding <= 16) {
      // Verify padding
      for (let i = 0; i < padding; i++) {
        if (decryptedBytes[decryptedBytes.length - 1 - i] !== padding) {
          return encryptedText // Invalid padding
        }
      }
      const unpaddedBytes = decryptedBytes.slice(0, decryptedBytes.length - padding)
      return AES.utils.utf8.fromBytes(unpaddedBytes)
    }
    return encryptedText
  } catch (error) {
    // If decryption fails, it might be unencrypted data or wrong key
    return encryptedText
  }
}

const encryptAndSetItem = async (key, value) => {
  try {
    const stringValue = JSON.stringify(value)
    const encryptedValue = encrypt(stringValue)
    await localforage.setItem(key, encryptedValue)
  } catch (error) {
    sentry.captureException(error)
  }
}

const decryptAndGetItem = async (key) => {
  try {
    const encryptedValue = await localforage.getItem(key)
    if (!encryptedValue) return null
    const decryptedValue = decrypt(encryptedValue)
    return JSON.parse(decryptedValue)
  } catch (error) {
    // We don't want to capture exception here if it is just a parse error
    // because it might be unencrypted data or corrupted data
    return null
  }
}

const encryptAndSetItemSync = (key, value) => {
  try {
    const stringValue = JSON.stringify(value)
    const encryptedValue = encrypt(stringValue)
    window.localStorage.setItem(key, encryptedValue)
  } catch (error) {
    sentry.captureException(error)
  }
}

const decryptAndGetItemSync = (key) => {
  try {
    const encryptedValue = window.localStorage.getItem(key)
    if (!encryptedValue) return null
    const decryptedValue = decrypt(encryptedValue)
    return JSON.parse(decryptedValue)
  } catch (error) {
    return null
  }
}

export const saveCachedCurrentUser = async (currentUser) => {
  await encryptAndSetItem(currentUserCacheKey, currentUser)
}

export const saveCachedUnleashContext = (unleashContext) => {
  encryptAndSetItemSync(unleashContextCacheKey, unleashContext)
}

export const clearCachedCurrentUser = async () => {
  await localforage.removeItem(currentUserCacheKey)
}

export const clearCachedUnleashContext = () => {
  window.localStorage.removeItem(unleashContextCacheKey)
}

export const clearAllCachedData = async () => {
  await Promise.all([clearCachedCurrentUser(), clearSavedProfiles()])
  clearCachedUnleashContext()
}

export const getCachedCurrentUser = async () => {
  const currentUser = await decryptAndGetItem(currentUserCacheKey)
  if (!currentUser || !currentUser.id) return null
  return currentUser
}

export const getCachedUnleashContext = () => {
  return decryptAndGetItemSync(unleashContextCacheKey)
}
